<template>  <teleport to="body">
    <AppModal
      :isVisible="isVisible"
      :title="isEditMode ? $t('finance.editPayment') : $t('finance.recordPayment')"
      @cancel="handleCancel"
    >
      <form @submit.prevent="handleSubmit" class="finance-form">
        <div class="form-group">
          <label>{{ $t('finance.amount') }}</label>
          <input
            type="number"
            v-model="formData.amount"
            step="0.01"
            required
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.paymentType') }}</label>
          <select v-model="formData.paymentType" required class="form-input">
            <option value="">{{ $t('finance.selectPaymentType') }}</option>
            <option value="CASH">{{ $t('finance.paymentTypes.cash') }}</option>
            <option value="BANK_TRANSFER">{{ $t('finance.paymentTypes.bankTransfer') }}</option>
            <option value="CHECK">{{ $t('finance.paymentTypes.check') }}</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.transactionDate') }}</label>
          <input
            type="date"
            v-model="formData.transactionDate"
            required
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.description') }}</label>
          <textarea
            v-model="formData.description"
            class="form-input"
          ></textarea>
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.invoice') }}</label>
          <input
            type="file"
            @change="handleInvoiceUpload"
            accept=".pdf,.jpg,.jpeg,.png"
            class="form-input"
          />
        </div>
        
        <div class="form-actions">
          <AppButton type="button" variant="outline" @click="handleCancel">
            {{ $t('common.cancel') }}
          </AppButton>
          <AppButton type="submit" variant="primary">
            {{ $t('common.save') }}
          </AppButton>
        </div>
      </form>
    </AppModal>
  </teleport>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, watch } from 'vue';
import AppButton from '@/components/base/AppButton.vue';
import AppModal from '@/components/base/Modal.vue';
import type { PaymentData } from '@/types/services';

interface Props {
  isVisible?: boolean;
  payment?: PaymentData | null;
  isEditMode?: boolean;
  provisioningRequest?: any | null;
}

interface Emits {
  (e: 'cancel'): void;
  (e: 'submit', payload: { data: FormDataType; isValid: boolean }): void;
}

interface FormDataType {
  amount: number | null;
  description: string;
  paymentType: string;
  transactionDate: string;
  invoice: File | null;
  provisioningRequestId?: number;
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
  payment: null,
  isEditMode: false,
  provisioningRequest: null
});

const emit = defineEmits<Emits>();

// Form data
const formData = ref<FormDataType>({
  amount: null,
  description: '',
  paymentType: '',
  transactionDate: new Date().toISOString().split('T')[0],
  invoice: null,
  provisioningRequestId: undefined
});

// Methods
const handleCancel = () => {
  resetForm();
  emit('cancel');
};

const handleSubmit = () => {
  // Validate required fields
  if (!formData.value.amount || !formData.value.paymentType || !formData.value.transactionDate) {
    // Parent component should handle validation error display
    emit('submit', { data: formData.value, isValid: false });
    return;
  }
  
  emit('submit', { data: formData.value, isValid: true });
};

const handleInvoiceUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  formData.value.invoice = target.files?.[0] || null;
};

const resetForm = () => {
  if (props.provisioningRequest) {
    // Initialize with provisioning request data
    formData.value = {
      amount: props.provisioningRequest.requestedAmount,
      description: props.provisioningRequest.description || '',
      paymentType: '',
      transactionDate: new Date().toISOString().split('T')[0],
      invoice: null,
      provisioningRequestId: props.provisioningRequest.id
    };
  } else if (props.isEditMode && props.payment) {
    // Initialize with existing payment data for editing
    formData.value = {
      amount: props.payment.amount,
      description: props.payment.description || '',
      paymentType: props.payment.paymentType || '',
      transactionDate: props.payment.transactionDate || new Date().toISOString().split('T')[0],
      invoice: null, // File input will be empty for editing
      provisioningRequestId: undefined
    };
  } else {
    // Initialize with empty data for new payment
    formData.value = {
      amount: null,
      description: '',
      paymentType: '',
      transactionDate: new Date().toISOString().split('T')[0],
      invoice: null,
      provisioningRequestId: undefined
    };
  }
  
  // Reset file input
  const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
  if (fileInput) {
    fileInput.value = '';
  }
};

// Watch for visibility changes to reset form when modal is opened
watch(() => props.isVisible, (newVal: boolean) => {
  if (newVal) {
    resetForm();
  }
});

// Watch for payment changes to update form data when editing
watch(() => props.payment, () => {
  if (props.isVisible) {
    resetForm();
  }
}, { deep: true });

// Watch for provisioning request changes to update form data
watch(() => props.provisioningRequest, () => {
  if (props.isVisible && props.provisioningRequest) {
    resetForm();
  }
}, { deep: true });
</script>

<style scoped>
.finance-form {
  padding: var(--space-md);
}

.form-group {
  margin-bottom: var(--space-md);
}

.form-group label {
  display: block;
  margin-bottom: var(--space-xs);
  font-weight: 500;
  color: var(--text-primary);
}

.form-input {
  width: 100%;
  padding: var(--space-sm);
  border: 1px solid var(--neutral-300);
  border-radius: var(--border-radius-sm);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm);
  margin-top: var(--space-lg);
}
</style>
